import React, { useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import {
  Api as ApiIcon,
} from "@mui/icons-material";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import type { EndpointRequest, HttpMethod, Priority } from "../types";

interface CreateRequestDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (request: Omit<EndpointRequest, "id">) => void;
}

const HTTP_METHODS: HttpMethod[] = ["GET", "POST", "PUT", "PATCH", "DELETE"];
const PRIORITIES: Priority[] = ["low", "medium", "high", "urgent"];

export const CreateRequestDialog: React.FC<CreateRequestDialogProps> = ({
  open,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<Omit<EndpointRequest, "id">>({
    component: "",
    task: "",
    lead: "",
    method: "GET",
    status: "pending",
    priority: "medium",
    requestDate: new Date().toISOString().split("T")[0],
    targetDeliveryDate: "",
    expectedDeliveryDate: "",
    challenges: "",
    description: "",
    payload: "",
  });

  const [requestDate, setRequestDate] = useState<Dayjs | null>(dayjs());
  const [targetDate, setTargetDate] = useState<Dayjs | null>(null);
  const [expectedDate, setExpectedDate] = useState<Dayjs | null>(null);

  const handleSubmit = () => {
    if (!formData.component || !formData.task || !formData.lead) {
      alert("Please fill in all required fields (Component, Task, Lead)");
      return;
    }

    const submitData = {
      ...formData,
      requestDate: requestDate?.format("YYYY-MM-DD") || formData.requestDate,
      targetDeliveryDate: targetDate?.format("YYYY-MM-DD") || undefined,
      expectedDeliveryDate: expectedDate?.format("YYYY-MM-DD") || undefined,
    };

    onSubmit(submitData);
    handleClose();
  };

  const handleClose = () => {
    setFormData({
      component: "",
      task: "",
      lead: "",
      method: "GET",
      status: "pending",
      priority: "medium",
      requestDate: new Date().toISOString().split("T")[0],
      targetDeliveryDate: "",
      expectedDeliveryDate: "",
      challenges: "",
      description: "",
      payload: "",
    });
    setRequestDate(dayjs());
    setTargetDate(null);
    setExpectedDate(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <ApiIcon />
          <Typography variant="h6">Create New Endpoint Request</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Component *"
                value={formData.component}
                onChange={(e) => setFormData({ ...formData, component: e.target.value })}
                placeholder="e.g., OLD MUTUAL DASHBOARD"
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Lead *"
                value={formData.lead}
                onChange={(e) => setFormData({ ...formData, lead: e.target.value })}
                placeholder="e.g., Dancel"
                required
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Task Description *"
                value={formData.task}
                onChange={(e) => setFormData({ ...formData, task: e.target.value })}
                placeholder="Describe the endpoint requirement..."
                multiline
                rows={3}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth>
                <InputLabel>HTTP Method</InputLabel>
                <Select
                  value={formData.method}
                  label="HTTP Method"
                  onChange={(e) =>
                    setFormData({ ...formData, method: e.target.value as HttpMethod })
                  }
                >
                  {HTTP_METHODS.map((method) => (
                    <MenuItem key={method} value={method}>
                      <Chip
                        label={method}
                        size="small"
                        sx={{
                          backgroundColor:
                            method === "GET"
                              ? "#4caf50"
                              : method === "POST"
                              ? "#2196f3"
                              : method === "PUT"
                              ? "#ff9800"
                              : method === "PATCH"
                              ? "#9c27b0"
                              : "#f44336",
                          color: "white",
                          fontWeight: "bold",
                        }}
                      />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Priority</InputLabel>
                <Select
                  value={formData.priority}
                  label="Priority"
                  onChange={(e) =>
                    setFormData({ ...formData, priority: e.target.value as Priority })
                  }
                >
                  {PRIORITIES.map((priority) => (
                    <MenuItem key={priority} value={priority}>
                      {priority.toUpperCase()}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <DatePicker
                label="Request Date"
                value={requestDate}
                onChange={(newValue: any) => setRequestDate(newValue as Dayjs | null)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 4 }}>
              <DatePicker
                label="Target Delivery Date"
                value={targetDate}
                onChange={(newValue: any) => setTargetDate(newValue as Dayjs | null)}
                slotProps={{
                  textField: {
                    fullWidth: true,
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Expected Delivery Date"
                value={formData.expectedDeliveryDate}
                onChange={(e) =>
                  setFormData({ ...formData, expectedDeliveryDate: e.target.value })
                }
                placeholder="YYYY-MM-DD"
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Payload/Request Body (JSON)"
                value={formData.payload}
                onChange={(e) => setFormData({ ...formData, payload: e.target.value })}
                placeholder='{"key": "value"}'
                multiline
                rows={4}
                sx={{
                  "& .MuiInputBase-input": {
                    fontFamily: "monospace",
                    fontSize: "0.875rem",
                  },
                }}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Additional Description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                multiline
                rows={3}
                placeholder="Any additional context or requirements..."
              />
            </Grid>
          </Grid>
        </LocalizationProvider>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={handleClose}>Cancel</Button>
        <Button onClick={handleSubmit} variant="contained" color="primary">
          Create Request
        </Button>
      </DialogActions>
    </Dialog>
  );
};

